import jsPDF from 'jspdf';
import { ReportAttachment } from '../types';

interface LoadedAttachment {
  fileName: string;
  width: number;
  height: number;
  jpegDataUrl: string;
}

// Attachment photos come straight from a phone camera (often 3000x4000px+, several MB each). Embedding
// them at full resolution as PNG would produce a multi-hundred-MB PDF, since PNG is lossless and
// photographic content barely compresses under it. Downscaling to a print-reasonable size and
// re-encoding as JPEG keeps file size sane while staying sharp enough on an A4 page.
const loadImageAsJpeg = (fileName: string, src: string, maxDimension = 1600): Promise<LoadedAttachment | null> =>
  new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      try {
        const naturalW = img.naturalWidth || img.width;
        const naturalH = img.naturalHeight || img.height;
        if (naturalW === 0 || naturalH === 0) {
          resolve(null);
          return;
        }
        const scale = Math.min(1, maxDimension / Math.max(naturalW, naturalH));
        const width = Math.round(naturalW * scale);
        const height = Math.round(naturalH * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        resolve({ fileName, width, height, jpegDataUrl: canvas.toDataURL('image/jpeg', 0.82) });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

const fitImage = (img: { width: number; height: number }, maxW: number, maxH: number) => {
  const ratio = Math.min(maxW / img.width, maxH / img.height);
  return { w: img.width * ratio, h: img.height * ratio };
};

const COLOR_TEXT = '#1a2942';
const COLOR_MUTED = '#64748b';

// Separate document from the report PDF itself: one photo stacked below the other, wrapping onto a
// new page only when the next one no longer fits — as opposed to generateReportPdf's one-full-page-
// per-attachment layout it used before this was split into its own "Scarica allegati" download.
export const generateAttachmentsPdf = async (attachments: ReportAttachment[]): Promise<Blob> => {
  const loaded = await Promise.all(attachments.map(a => loadImageAsJpeg(a.fileName, a.previewUrl)));
  const validAttachments = loaded.filter((a): a is LoadedAttachment => a !== null);

  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const PAGE_WIDTH = pdf.internal.pageSize.getWidth();
  const PAGE_HEIGHT = pdf.internal.pageSize.getHeight();
  const MARGIN = 28;
  const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
  const CONTENT_HEIGHT = PAGE_HEIGHT - MARGIN * 2;
  const CAPTION_H = 14;
  const GAP = 16;

  let y = MARGIN;
  let hasDrawnAny = false;

  validAttachments.forEach((attachment, idx) => {
    const { w, h } = fitImage(attachment, CONTENT_WIDTH, CONTENT_HEIGHT - CAPTION_H);
    const blockH = CAPTION_H + h;

    if (hasDrawnAny && y + GAP + blockH > PAGE_HEIGHT - MARGIN) {
      pdf.addPage();
      y = MARGIN;
    } else if (hasDrawnAny) {
      y += GAP;
    }

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(COLOR_TEXT);
    pdf.text(`${idx + 1}. ${attachment.fileName}`, MARGIN, y + 8);
    y += CAPTION_H;

    pdf.addImage(attachment.jpegDataUrl, 'JPEG', MARGIN, y, w, h);
    y += h;
    hasDrawnAny = true;
  });

  if (!hasDrawnAny) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(COLOR_MUTED);
    pdf.text('Nessun allegato disponibile.', MARGIN, MARGIN + 10);
  }

  return pdf.output('blob');
};
